import { NetworkStatus, gql, useQuery } from '@apollo/client';
import IconRocket from '@apollo/icons/large/IconRocket.svg';
import {
  Card,
  CardDescription,
  CardHeader,
  Group,
  Heading,
} from '@apollo/orbit';
import { partition } from 'lodash';
import React, { useEffect, useMemo } from 'react';

import { Loading } from 'src/components/common/loading/Loading';
import { test } from 'src/components/common/typeahead/fuzzy';
import { ampli } from 'src/lib/analytics/amplitude/vendor';
import { ignorePermissionsErrors } from 'src/lib/apollo/catchErrors';
import { appLinkContext } from 'src/lib/apollo/link';
import { format } from 'src/lib/format';
import * as ApolloTypes from 'src/lib/graphqlTypes/types';

import { GraphErrorDisplay } from './GraphErrorDisplay';
import { NoVariantsSupergraphsListItem } from './NoVariantsSupergraphsListItem';
import {
  SupergraphsListItem,
  SupergraphsListItemGraphFragment,
} from './SupergraphsListItem';

export const SupergraphsListServiceFragment = gql<
  ApolloTypes.SupergraphsListServiceFragment,
  never
>`
  fragment SupergraphsListServiceFragment on Service {
    id
    graphType
    title
    avatarUrl
    variants {
      id
      isFavoriteOfCurrentUser
    }
    ...SupergraphsListItemGraphFragment
  }
  ${SupergraphsListItemGraphFragment}
`;

export const supergraphsListQuery = gql<
  ApolloTypes.SupergraphsListQuery,
  ApolloTypes.SupergraphsListQueryVariables
>`
  query SupergraphsListQuery($accountId: ID!) {
    account(id: $accountId) {
      id
      graphsConnection {
        edges {
          node {
            id
            ...SupergraphsListServiceFragment
          }
        }
      }
    }
  }
  ${SupergraphsListServiceFragment}
`;

export const SupergraphsList = ({
  accountId,
  supergraphListSearch,
}: {
  accountId: string;
  supergraphListSearch: string | undefined;
}) => {
  const { data, networkStatus, error } = useQuery(supergraphsListQuery, {
    errorPolicy: 'all',
    notifyOnNetworkStatusChange: true,
    pollInterval: 30_000,
    variables: {
      accountId,
    },
    context: appLinkContext({ catchErrors: [ignorePermissionsErrors] }),
  });

  // Filter All Graphs by Type (For Rendering, specifically when search is used)
  const filteredGraphs = useMemo(
    () =>
      data?.account?.graphsConnection?.edges?.filter(
        ({ node }) =>
          node && test(supergraphListSearch || '', node.title || ''),
      ),
    [data, supergraphListSearch],
  );

  // Group favorite graphs
  const graphsWithFavs = useMemo(
    () =>
      data?.account?.graphsConnection?.edges?.filter(
        ({ node }) =>
          node &&
          node.variants.some(
            ({ isFavoriteOfCurrentUser }) => !!isFavoriteOfCurrentUser,
          ),
      ) || [],
    [data],
  );

  // Partition Graphs by Published State (For Metrics)
  // - Published graphs have one or more variants
  // - Unpublished graphs have zero variants
  const [publishedGraphs, unpublishedGraphs] = useMemo(() => {
    return partition(
      filteredGraphs,
      ({ node }) => node && node.variants.length > 0,
    );
  }, [filteredGraphs]);

  // Side Effect for Metrics Tracking
  // Note: This tracks metrics only when the graph data (which is cached) changes,
  // avoiding unnecessary triggers from polling or user searches
  useEffect(() => {
    const totalGraphs = publishedGraphs.length + unpublishedGraphs.length;
    // Proceed only if there are graphs
    if (totalGraphs > 0) {
      ampli.homePageViewed({
        'Total Graphs': totalGraphs,
        'Deployed Graphs': publishedGraphs.length,
        'Unconfigured Graphs': unpublishedGraphs.length,
      });
    }
    // Note: We include published Graphs and unpublished Graphs in dependencies
    // because they are derived from allGraphsGroupedByType and are necessary for metrics
  }, [publishedGraphs.length, unpublishedGraphs.length]);

  if (networkStatus === NetworkStatus.loading && !data)
    return <Loading size="md" />;

  if (error) return <GraphErrorDisplay error={error} />;

  if (!data || !data.account)
    return (
      <GraphErrorDisplay
        error={{
          message: (
            <>
              Organization <span className="font-mono">{accountId}</span> was
              not found
            </>
          ),
        }}
      />
    );

  return (
    <>
      {supergraphListSearch && filteredGraphs?.length === 0 ? (
        <>
          <Heading className="mb-8 mt-10 h-4" size="2xl">
            0 matching graphs
          </Heading>
          <div>
            There are no matching graphs, please change your search parameter.
          </div>
        </>
      ) : null}
      {!supergraphListSearch && !!graphsWithFavs.length && (
        <>
          <Heading className="mb-8 mt-10 h-4" size="2xl">
            {format.pluralize(graphsWithFavs.length, 'Favorite')}
          </Heading>
          {graphsWithFavs.map(
            ({ node: graph }) =>
              graph && (
                <SupergraphsListItem
                  searchText={supergraphListSearch}
                  key={graph.id}
                  graph={graph}
                  isInFavSection
                />
              ),
          )}
        </>
      )}
      {publishedGraphs.length > 0 && (
        <>
          <Heading className="mb-8 mt-10 flex h-4 items-center" size="2xl">
            {format.pluralize(publishedGraphs.length, 'Published graph')}
          </Heading>
          {publishedGraphs.map(
            ({ node: graph }) =>
              graph && (
                <SupergraphsListItem
                  searchText={supergraphListSearch}
                  key={graph.id}
                  graph={graph}
                />
              ),
          )}
        </>
      )}
      {unpublishedGraphs.length > 0 && publishedGraphs.length === 0 && (
        <Card className="mb-8 mt-10">
          <CardHeader>
            <Group orientation="horizontal" gap={4} align="start">
              <div className="flex items-center rounded-md border border-primary bg-secondary p-4">
                <IconRocket className="size-7" />
              </div>
              <Group orientation="vertical" gap={2}>
                <Heading size="xl">Almost there</Heading>
                <CardDescription>
                  Complete your setup to unlock GraphOS features like
                  visualization, checks, and monitoring.
                </CardDescription>
              </Group>
            </Group>
          </CardHeader>
        </Card>
      )}
      {unpublishedGraphs.map(
        ({ node: graph }) =>
          graph && (
            <NoVariantsSupergraphsListItem
              graphId={graph.id}
              graphTitle={graph.title}
              graphType={graph.graphType}
              avatarUrl={graph.avatarUrl}
              searchText={supergraphListSearch}
            />
          ),
      )}
    </>
  );
};
